import { API_URL } from "../config/config";
import { extractErrorMessage } from "../helpers/error.helper";
import { parseJwt } from "../helpers/jwt.helper";
import { RequestFn } from "../api/useApi";

export type AuthResult = {
    accessToken: string;
    expiresAt: string;
};

export type MeDto = {
    id: string;
    email: string;
    displayName?: string | null;
    roles: string[];
};

export type AppUser = { id: string; email: string; fullName: string; roles: string[] };

export type RegisterRequest = {
    email: string;
    password: string;
    displayName?: string | null;
    userRole: string;
};

export type ConfirmEmailRequest = {
    userId: string;
    token: string;
};

export type ChangePasswordRequest = {
    currentPassword: string;
    newPassword: string;
};

const ROLE_CLAIM = "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";

function normalizeRoles(value: unknown): string[] {
    if (Array.isArray(value)) {
        return value.filter((entry): entry is string => typeof entry === "string");
    }

    if (typeof value === "string") {
        return [value];
    }

    return [];
}

async function registerRequest(payload: RegisterRequest): Promise<AuthResult> {
    const res = await fetch(`${API_URL}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
    });

    if (!res.ok) {
        let msg = `Registration failed (${res.status})`;
        try {
            const problem = await res.json();
            msg = extractErrorMessage(problem, msg);
        } catch { }
        throw new Error(msg);
    }

    return res.json();
}

async function loginRequest(email: string, password: string): Promise<AuthResult> {
    const res = await fetch(`${API_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
    });

    if (!res.ok) {
        let msg = "Invalid credentials";
        try {
            const problem = await res.json();
            msg = extractErrorMessage(problem, msg);
        } catch { }
        throw new Error(msg);
    }

    return res.json();
}

async function confirmEmail(payload: ConfirmEmailRequest): Promise<{ message: string }> {
    const res = await fetch(`${API_URL}/auth/confirm-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
    });

    if (!res.ok) {
        let msg = "Email confirmation failed";
        try {
            const data = await res.json();
            msg = extractErrorMessage(data, msg);
        } catch {
            try {
                const text = await res.text();
                if (text) msg = text;
            } catch { }
        }
        throw new Error(msg);
    }

    return res.json();
}

async function resendConfirmationEmail(email: string): Promise<{ message: string }> {
    const res = await fetch(`${API_URL}/auth/resend-confirmation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email }),
    });

    if (!res.ok) {
        let msg = "Could not resend confirmation email";
        try {
            const data = await res.json();
            msg = extractErrorMessage(data, msg);
        } catch {
            try {
                const text = await res.text();
                if (text) msg = text;
            } catch { }
        }
        throw new Error(msg);
    }

    return res.json();
}

async function fetchMe(accessToken: string): Promise<MeDto | null> {
    const res = await fetch(`${API_URL}/auth/me`, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
        credentials: "include",
    });

    if (!res.ok) return null;
    return res.json();
}

async function changePassword(payload: ChangePasswordRequest, request: RequestFn): Promise<void> {
    const res = await request(`${API_URL}/auth/me`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            currentPassword: payload.currentPassword,
            newPassword: payload.newPassword,
        }),
    });

    if (!res.ok) {
        let msg = "Could not update password";
        try {
            const data = await res.json();
            msg = extractErrorMessage(data, msg);
        } catch {
            try {
                const text = await res.text();
                if (text) msg = text;
            } catch { }
        }
        throw new Error(msg);
    }
}

function buildUserFromJwt(accessToken: string): AppUser | null {
    const payload = parseJwt<Record<string, unknown>>(accessToken);
    if (!payload) return null;

    const id =
        payload.sub ||
        payload.nameid ||
        payload["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"];

    const emailFromJwt =
        payload.email ||
        payload["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"];

    if (!id || !emailFromJwt) return null;

    const roles = normalizeRoles(payload.roles).concat(normalizeRoles(payload.role), normalizeRoles(payload[ROLE_CLAIM]));

    return {
        id: String(id),
        email: String(emailFromJwt),
        fullName: String(payload.name || String(emailFromJwt).split("@")[0]),
        roles,
    };
}

export const authService = {
    registerRequest,
    loginRequest,
    confirmEmail,
    resendConfirmationEmail,
    fetchMe,
    changePassword,
    buildUserFromJwt,
};
